import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { createCheckoutSession, hasActiveSubscription, getProducts } from '../services/stripeService';
import { Button, Card, Alert, Badge, Spinner, Row, Col, Container } from 'react-bootstrap';
import { getStatusBadgeVariant } from '../utils/subscriptionUtils';

const Subscription = () => {
  const { user, userData } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isActiveSubscription, setIsActiveSubscription] = useState(false);
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Check subscription status when component mounts or userData changes
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (userData) {
        try {
          const hasActive = await hasActiveSubscription();
          setIsActiveSubscription(hasActive);
        } catch (error) {
          console.error('Error checking subscription status:', error);
          setIsActiveSubscription(false);
        }
      }
    };

    checkSubscriptionStatus();
  }, [userData]);

  // Fetch products and prices when component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        console.log('🔄 Subscription: Starting to fetch products...');
        setProductsLoading(true);
        setError('');
        
        const response = await getProducts();
        console.log('✅ Subscription: API response received:', response);
        
        setProducts(response.products || []);
        setPrices(response.prices || []);
        
        console.log('🎯 Subscription: State updated successfully');
      } catch (error) {
        console.error('❌ Subscription: Error fetching products:', error);
        setError('Failed to load subscription options. Please try again later.');
      } finally {
        setProductsLoading(false);
        console.log('🏁 Subscription: fetchProducts completed');
      }
    };

    fetchProducts();
  }, []);

  const handleSubscribe = async (priceId) => {
    if (!user) {
      setError('Please log in to subscribe');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const successUrl = `${window.location.origin}/success`;
      const cancelUrl = `${window.location.origin}/subscription`;
      
      console.log('🛒 Creating checkout session with:', {
        priceId,
        successUrl,
        cancelUrl
      });
      
      const checkoutUrl = await createCheckoutSession(priceId, successUrl, cancelUrl);
      console.log('✅ Checkout URL received:', checkoutUrl);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format price
  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    
    const amount = price.unitAmount / 100; // Convert from cents
    const currency = price.currency.toUpperCase();
    
    if (price.interval) {
      return `£${amount.toFixed(2)}/${price.interval}`;
    }
    
    return `£${amount.toFixed(2)} ${currency}`;
  };

  // Get prices for a specific product
  const getProductPrices = (productId) => {
    console.log('🔍 getProductPrices called with productId:', productId);
    console.log('🔍 Available prices:', prices);
    
    const filteredPrices = prices.filter(price => {
      // Handle both string and object productId structures
      const priceProductId = typeof price.productId === 'object' ? price.productId.id : price.productId;
      console.log('🔍 Comparing:', priceProductId, '===', productId);
      return priceProductId === productId;
    });
    
    console.log('🔍 Filtered prices for product:', productId, ':', filteredPrices);
    return filteredPrices;
  };

  if (!user) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Please log in to view subscription options.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <div className="text-center mb-5">
        <h1>Choose Your Plan</h1>
        <p className="lead text-muted">
          Select a subscription plan to access all premium features
        </p>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Current Status */}
      <Row className="mb-4">
        <Col md={12}>
          <Card className="text-center">
            <Card.Body>
              <h5>Current Status</h5>
              <Badge 
                bg={isActiveSubscription ? 'success' : 'secondary'} 
                style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}
              >
                {isActiveSubscription ? '✓ Active Subscription' : 'No Active Subscription'}
              </Badge>
              {isActiveSubscription && (
                <p className="text-muted mt-2 mb-0">
                  You have access to all premium features
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Subscription Plans */}
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>
              <h4 className="mb-0">Available Plans</h4>
            </Card.Header>
            <Card.Body>
              {productsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading subscription options...</span>
                </div>
              ) : products.length > 0 ? (
                <Row>
                  {products.map((product) => {
                    console.log('🎯 Rendering product:', product);
                    const productPrices = getProductPrices(product.id);
                    console.log('🎯 Product prices for', product.name, ':', productPrices);
                    return (
                      <Col md={6} lg={4} key={product.id} className="mb-4">
                        <Card className="h-100 border-primary">
                          <Card.Body className="text-center">
                            <h5 className="card-title">{product.name}</h5>
                            {product.description && (
                              <p className="text-muted small">{product.description}</p>
                            )}
                            
                            {productPrices.map((price) => (
                              <div key={price.id} className="mb-3">
                                <div className="display-6 fw-bold text-primary mb-2">
                                  {formatPrice(price)}
                                </div>
                                <Button
                                  variant={isActiveSubscription ? "outline-primary" : "primary"}
                                  size="lg"
                                  onClick={() => handleSubscribe(price.id)}
                                  disabled={loading || isActiveSubscription}
                                  className="w-100"
                                >
                                  {loading ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : isActiveSubscription ? (
                                    'Current Plan'
                                  ) : (
                                    'Buy Now'
                                  )}
                                </Button>
                              </div>
                            ))}
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <Alert variant="info" className="text-center">
                  <h5>No subscription plans available</h5>
                  <p className="mb-0">
                    Please contact support if you believe this is an error.
                  </p>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Account Information section removed - users don't need to see technical details */}
    </Container>
  );
};

export default Subscription;
